/// <reference types="react" />

import "VSS/LoaderPlugins/Css!Agile/Scripts/Backlog/ContextMenu/Components/ChangeParentDialog";

import React = require("react");
import { Fabric } from "OfficeFabric/Fabric";
import * as Dialog from "OfficeFabric/Dialog";
import * as Button from "OfficeFabric/Button";
import * as Utils_String from "VSS/Utils/String";
import * as AgileProductBacklogResources from "Agile/Scripts/Resources/TFS.Resources.AgileProductBacklog";
import { IBacklogLevelConfiguration } from "Presentation/Scripts/TFS/FeatureRef/BacklogConfiguration/Models";
import { ITeam, TeamBuilder } from "Agile/Scripts/Models/Team";
import * as BacklogContentViewResources from "Agile/Scripts/Resources/TFS.Resources.BacklogsHub.BacklogView";
import { format } from "VSS/Utils/String";
import { LoadingComponent } from "Presentation/Scripts/TFS/Components/LoadingComponent";
import { getDefaultWebContext } from "VSS/Context";
import { getClient as getCoreClient } from "TFS/Core/RestClient";
import { WebApiTeam } from "TFS/Core/Contracts";
import { TeamPicker } from "./TeamPicker";
import { ParentPicker } from "./ParentPicker";

export interface IChangeParentDialogProps {
    /** The ids of work items to reparent */
    workItemIdsToReparent: number[];

    /** The initially selected team id */
    initialTeamId: string;

    /** The parent backlog level items this dialog will display to select from */
    parentBacklogLevel: IBacklogLevelConfiguration;

    /** Callback with id of new parent */
    onSave: (parentId: number) => void;

    /** Callback to dismiss dialog */
    onDismiss: () => void;
}

export const ChangeParentDialog = (props: IChangeParentDialogProps) => {
    const [selectedTeam, setSelectedTeam] = React.useState(undefined as ITeam);
    const [errorMessage, setErrorMessage] = React.useState(undefined as string);
    const [selectedParentId, setSelectedParentId] = React.useState(undefined as number);

    const projectId = getDefaultWebContext().project.id;

    // Load initial team by id
    React.useEffect(() => {
        (async () => {
            try {
                const restClient = getCoreClient();
                const team: WebApiTeam = await restClient.getTeam(projectId, props.initialTeamId);
                setSelectedTeam(TeamBuilder.fromWebApiTeam(team));
            } catch (error) {
                setErrorMessage(format(AgileProductBacklogResources.ChangeParentErrorLoadingTeamsMessage, error.message));
            }
        })();
    }, []);

    const CSS_Change_Parent_Dialog_Container = "change-parent-dialog";

    const render = (): JSX.Element => {
        const { onDismiss } = props;

        const dialogProps: Dialog.IDialogProps = {
            type: Dialog.DialogType.close,
            containerClassName: CSS_Change_Parent_Dialog_Container,
            title: AgileProductBacklogResources.BacklogsChangeParentDialogTitle,
            onDismiss: onDismiss,
            isBlocking: true,
            isOpen: true
        };

        return (
            <Fabric>
                <Dialog.Dialog {...dialogProps}>
                    {_renderContent()}
                    {_renderDialogFooter()}
                </Dialog.Dialog>
            </Fabric>
        );
    };

    const _renderContent = (): JSX.Element => {
        const { workItemIdsToReparent } = props;
        if (errorMessage) {
            return <div>{errorMessage}</div>;
        } else if (selectedTeam) {
            return (
                <div className="change-parent-dialog-content">
                    <p>{Utils_String.format(AgileProductBacklogResources.BacklogsChangeParentDialogDescription, workItemIdsToReparent.length)}</p>
                    {_renderTeamPicker()}
                    {_renderParentPicker()}
                </div>
            );
        }
        return <LoadingComponent />;
    };

    const _renderTeamPicker = (): JSX.Element => {
        const { parentBacklogLevel } = props;

        return (
            <div className="change-parent-dialog-team-header flex-row">
                {format(BacklogContentViewResources.MappingPane_BacklogForTeam, parentBacklogLevel.name)}
                <TeamPicker projectId={projectId} selectedTeam={selectedTeam} onTeamSelected={_onTeamSelected} />
            </div>
        );
    };

    const _onTeamSelected = (team: ITeam): void => {
        if (!Utils_String.equals(selectedTeam.id, team.id, true)) {
            setSelectedTeam(team);
        }
    };

    const _renderParentPicker = (): JSX.Element => {
        if (!selectedTeam) {
            return <LoadingComponent />;
        }
        return (
            <ParentPicker
                projectId={projectId}
                selectedTeam={selectedTeam}
                parentBacklogLevelId={props.parentBacklogLevel.id}
                selectedParentId={selectedParentId}
                setSelectedParentId={setSelectedParentId}
            />
        );
    };

    const _renderDialogFooter = (): JSX.Element => {
        const { onSave, onDismiss } = props;

        return (
            <Dialog.DialogFooter>
                <Button.PrimaryButton disabled={!selectedParentId} onClick={() => onSave(selectedParentId)}>
                    {AgileProductBacklogResources.BacklogsChangeParentDialogFooterOk}
                </Button.PrimaryButton>
                <Button.DefaultButton disabled={false} onClick={() => onDismiss()}>
                    {AgileProductBacklogResources.BacklogsChangeParentDialogFooterCancel}
                </Button.DefaultButton>
            </Dialog.DialogFooter>
        );
    };

    return render();
};
